import { defineStore } from 'pinia'
import UsersService from '@/api/UsersService'
import { useUserStore } from './user'
import { useAuthStore } from './auth'

export const useUsersStore = defineStore('users', {
    state: () => ({
        usersTableRefreshKey: 0,
        loading: false,
        deleting: false,
        resending: false,
        loadingUserRoles: false,
        togglingUserRoles: false,
        sending: false,
    }),

    getters: {},

    actions: {
        _userStore() {
            return useUserStore()
        },

        _authStore() {
            return useAuthStore()
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        async resendInvite(id) {
            this.resending = true
            try {
                await UsersService.resendInvite(id)
                this.notification('success', '§ui_message.resend_successful')
                this.usersTableRefreshKey += 1
            } catch (error) {
                console.error(`Error resending invite id=${id} (stores/users/resendInvite):`, error)
                this.notification('error', '§ui_message.resend_failed')
            } finally {
                this.resending = false
            }
        },

        async deleteInvite({ id }) {
            this.deleting = true
            try {
                await UsersService.deleteInvite(id)
                this.notification('success', '§ui_message.delete_successful')
                this.usersTableRefreshKey += 1
            } catch (error) {
                console.error(`Error deleting invite id=${id} (stores/users/deleteInvite):`, error)
                this.notification('error', '§ui_message.delete_failed')
            } finally {
                this.deleting = false
            }
        },

        async deleteRegularUser({ id }) {
            this.deleting = true
            try {
                await UsersService.deleteRegularUser(id)
                this.notification('success', '§ui_message.delete_successful')
                this.usersTableRefreshKey += 1
            } catch (error) {
                console.error(
                    `Error deleting user id=${id} (stores/users/deleteRegularUser):`,
                    error
                )
                this.notification('error', '§ui_message.delete_failed')
            } finally {
                this.deleting = false
            }
        },

        async getUserRoles(id) {
            this.loadingUserRoles = true
            try {
                const { data } = await UsersService.getUserRoles(id)
                return data
            } catch (error) {
                console.error(
                    `Error loading user roles id=${id} (stores/users/getUserRoles):`,
                    error
                )
                this.notification('error', '§ui_message.load_failed')
            } finally {
                this.loadingUserRoles = false
            }
        },

        async toggleUserRole(id, roleName) {
            this.togglingUserRoles = true
            try {
                await UsersService.toggleUserRole(id, { role: roleName })
                this.notification('success', '§ui_message.toggle_successful')
            } catch (error) {
                console.error(
                    `Error toggling user roles id=${id} role=${roleName} (stores/users/toggleUserRole):`,
                    error
                )
                this.notification('error', '§ui_message.toggle_failed')
            } finally {
                this.togglingUserRoles = false
            }
        },

        async sendInvite(data) {
            this.sending = true
            try {
                await UsersService.sendInvite(data)
                this.notification('success', '§ui_message.invite_sent')
            } catch (error) {
                console.error(`Error sending invite (stores/users/sendInvite):`, error)
                this.notification('error', '§ui_message.invite_failed')
            } finally {
                this.sending = false
            }
        },

        async sendPartnerInvite(data) {
            this.sending = true
            try {
                await UsersService.sendPartnerInvite(data)
                this.notification('success', '§ui_message.invite_sent')
            } catch (error) {
                console.error(`Error sending partner invite (stores/users/sendPartnerInvite):`, error)
                this.notification('error', '§ui_message.invite_failed')
            } finally {
                this.sending = false
            }
        },

        async createOem(data) {
            this.sending = true
            try {
                await UsersService.createOem(data)
                this.notification('success', '§ui_message.oem_created')
            } catch (error) {
                console.error(`Error creating OEM (stores/users/createOem):`, error)
                this.notification('error', '§ui_message.oem_creation_failed')
            } finally {
                this.sending = false
            }
        },

        async impersonateUser(id) {
            try {
                await UsersService.impersonateUser(id)
                this._userStore().resetUser(id)
                this._authStore().setUserId(id)
                this._userStore().getUserData(id)
                this.notification('success', '§ui_message.impersonation_started')
                window.location.pathname = '/app/home'
            } catch (error) {
                console.error(
                    `Error impersonating user id=${id} (stores/users/impersonateUser):`,
                    error
                )
                this.notification('error', '§ui_message.impersonation_failed')
            }
        },
    },
})
